import React, {useRef, useState, useEffect} from 'react';
import './ViewUsers.scss';
import TopHeader from './top-panel/TopHeader';
import FilterPanel from './filter-panel/FilterPanel';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {
  ROLE_OPTIONS,
  roleColumnVisibilityMap,
  userColumns,
  userDetailsMoreMenu,
  userDetailsRemoveMoreMenu,
} from '../userManagementUtils';
import {
  useDeleteUser,
  useRemoveUser,
  useUsers,
} from '../../../hooks/users/useUsers';
import {IGetUsersRequestPayload} from '../../../api/types/user.type';
import {GridRenderCellParams, GridSortModel} from '@mui/x-data-grid';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as MoreIcon} from '../../../assets/images/icon-more.svg';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../common/arr-menu/ArrMenu';
import {PAGE_SIZE, TOAST_DURATION} from '../../../shared/constants';
import {t} from 'i18next';
import ArrModal from '../../common/arr-modal/ArrModal';
import {ReactComponent as CloseToast} from '../../../../src/assets/images/close-toast.svg';
import ArrToast from '../../common/arr-toast/ArrToast';
import {UserRowData} from '../../../shared/interfaces';
import EditUser from '../edit-user/EditUser';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {useLocation, useNavigate} from 'react-router-dom';

const cleanPayload = (
  payload: IGetUsersRequestPayload
): IGetUsersRequestPayload => {
  return Object.fromEntries(
    Object.entries(payload).filter(([_, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    })
  ) as IGetUsersRequestPayload;
};

const ROW_HEIGHT = 36;

// -------------- Component -----------------
const ViewUsers: React.FC = () => {
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );
  const entityId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.entityId
  );
  const projectId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.projectId
  );
  const userId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.userId
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isActive, setIsActive] = useState(false);
  const [isDeleteUser, setIsDeleteUser] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserRowData | null>(null);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [isEditViewOpen, setIsEditViewOpen] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'firstName',
      sort: 'asc',
    },
  ]);

  const hiddenCols = roleColumnVisibilityMap[roleId || 0] || [];

  const visibleColumns = userColumns.filter(
    (col) => !hiddenCols.includes(col.field)
  );

  const [appliedFilters, setAppliedFilters] = useState({
    filterRoleId: 0,
    filterUserName: '',
    createdOn: '',
  });

  const location = useLocation();
  const navigate = useNavigate();

  const payload = cleanPayload({
    ...appliedFilters,
    sortBy: sortModel[0]?.field || '',
    sortingOrder: sortModel[0]?.sort || 'asc',
    pageNumber,
    pageSize,
    isActive,
  });
  const {
    data: users,
    isLoading: isUsersDataLoading,
    refetch: refetchUsers,
  } = useUsers(
    {
      ...payload,
      isActive: !payload.isActive,
    },
    {roleId, entityId, projectId}
  );
  // If navigated here with filters in location.state (e.g. after creating a user), apply them
  useEffect(() => {
    const state = (location as any)?.state;
    if (state && (state.filters || state.showSuccess)) {
      if (state.filters) {
        setAppliedFilters((prev) => ({
          ...prev,
          ...state.filters,
        }));
        setPageNumber(1);
      }
      if (state.showSuccess) {
        setShowToast(true);
        // Use a context-appropriate message if provided, else fallback
        setErrorMessage(state.successMessage || t('common.success'));
        // Always refetch users to ensure grid is up to date
        if (typeof refetchUsers === 'function') refetchUsers();
      }
      // clear the navigation state so this only runs once
      navigate(location.pathname, {replace: true, state: {}});
    }
  }, [location, navigate, refetchUsers, t]);
  const {
    data: deleteUserData,
    mutate: deleteUser,
    error: deleteUserError,
  } = useDeleteUser();

  const usersWithTempId = users?.data.map((u, index) => ({
    ...u,
    tempId: index + 1,
  }));

  const {
    mutate: removeUser,
    data: removeUserData,
    error: removeUserError,
  } = useRemoveUser();
  const [deleteError, setDeleteError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const menuRef1 = useRef<ArrMenuHandle | null>(null);

  const handleMoreIconClick = (
    e: React.MouseEvent<HTMLElement>,
    userData: UserRowData
  ) => {
    menuRef1.current?.open(e.currentTarget as HTMLElement);
    setSelectedUser(userData);
  };

  const onMenuItemClick = (item: CustomMenuItem) => {
    // Step 1: close menu first
    menuRef1.current?.close();

    // Step 2: wait for menu transition
    setTimeout(() => {
      if (item.label === 'Delete' || item.label === 'Remove') {
        setIsDeleteUser(true);
      } else if (item.label === 'Edit') {
        setIsEditViewOpen(true);
      }
    }, 0);
  };

  const ConfirmDelete = () => {
    if (roleId === -1) {
      if (selectedUser?.userId === userId) {
        setErrorMessage(t('userManagement.deleteUser.deleteLoggedIn'));
        setDeleteError(true);
        setShowToast(true);
      } else {
        const deletePayload = {
          userId: selectedUser?.userId ?? 0,
          modifiedBy: userId,
        };
        console.log('deleteUser payload:', deletePayload);
        deleteUser(deletePayload, {
          onSuccess: () => {
            setShowToast(true);
            if (typeof refetchUsers === 'function') refetchUsers();
          },
          onError: (err) => {
            console.error('deleteUser error:', err);
            setShowToast(true);
          },
        });
      }
    } else {
      if (selectedUser?.userId === userId) {
        setErrorMessage(t('userManagement.deleteUser.removeLoggedIn'));
        setDeleteError(true);
        setShowToast(true);
        setIsDeleteUser(false);
        return;
      }
      const entityID = selectedUser?.role === '-2' ? selectedUser?.entityID : 0;
      const params = [
        `userId=${selectedUser?.userId}`,
        `EntityID=${entityID}`,
        `Role=${Number(selectedUser?.role)}`,
        `EmailID=${selectedUser?.emailID}`,
      ];
      const queryParams = params.length > 0 ? `?${params.join('&')}` : '';
      const removePayload = {
        projectId: selectedUser?.projectID ?? 0,
        payload: queryParams,
      };
      console.log('removeUser payload:', removePayload);
      removeUser(removePayload, {
        onSuccess: () => {
          setShowToast(true);
          if (typeof refetchUsers === 'function') refetchUsers();
        },
        onError: (err) => {
          console.error('removeUser error:', err);
          setShowToast(true);
        },
      });
    }
    setIsDeleteUser(false);
  };

  const updatedColumns = [
    ...visibleColumns,
    {
      field: '',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      headerClassName: 'typo-text label2_size more-option-header',
      renderCell: (params: GridRenderCellParams) => {
        const {userId, entityID, role, emailID, projectID} = params.row;
        return (
          <ArrIconButton
            onClick={(e) =>
              handleMoreIconClick(e, {
                userId,
                entityID,
                role,
                emailID,
                projectID,
              })
            }
            icon={<MoreIcon />}
            ariaLabel=""
            shape="none"
            testId={`user-more-action-btn-${params.id}`}
            className="user-grid-icon-button"
          />
        );
      },
    },
  ];

  const handleApplyFilters = (
    nameOrEmail: string,
    roleId: number,
    date: string
  ) => {
    setPageNumber(1);
    setAppliedFilters({
      filterRoleId: roleId,
      filterUserName: nameOrEmail,
      createdOn: date ? new Date(date).toISOString() : '',
    });
  };

  const onPageChangeHandler = (value: number) => {
    setPageNumber(value);
  };

  const onPageSizeChangeHandler = (value: number) => {
    setPageSize(value);
    setPageNumber(1);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleCloseToast = () => {
    setShowToast(false);
    setErrorMessage('');
    setDeleteError(false);
  };
  return (
    <div className="view-users" data-testid={`view-users-div`}>
      <TopHeader onUserAdded={refetchUsers} />
      <FilterPanel
        initialNameOrEmail={appliedFilters.filterUserName}
        initialRoleId={
          ROLE_OPTIONS.find(
            (r) => Number(r.id) === appliedFilters.filterRoleId
          ) || ROLE_OPTIONS[0]
        }
        initialDate={appliedFilters.createdOn}
        initialIsActive={isActive}
        onApplyFilters={handleApplyFilters}
        onIsActiveChange={setIsActive}
      />
      <div data-testid="users-table">
        <ArrDataGrid
          rowHeight={ROW_HEIGHT}
          rows={usersWithTempId || []}
          columns={updatedColumns}
          rowCount={users?.totalRecords}
          currentPage={pageNumber}
          setCurrentPage={setPageNumber}
          itemsPerPage={pageSize}
          setItemsPerPage={onPageSizeChangeHandler}
          paginationMode="server"
          onPageChange={onPageChangeHandler}
          entityType={t('userManagement.manageUsers.users')}
          primaryKey="tempId"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          className="view-users-grid"
          loading={isUsersDataLoading}
          showPerPageOptions={true}
          pageSizeOptions={[10, 25, 50, 100]}
          data-testid="view-users-data-grid"
        />
      </div>
      <ArrMenu
        id="more-menu"
        ref={menuRef1}
        items={roleId === -1 ? userDetailsMoreMenu : userDetailsRemoveMoreMenu}
        onItemClick={onMenuItemClick}
        direction="left"
      />
      {isDeleteUser && (
        <ArrModal
          showModal={isDeleteUser}
          showClose={true}
          onClose={() => setIsDeleteUser(false)}
          headerTitle={t('userManagement.deleteUser.justToConfirm')}
          title={
            roleId === -1
              ? t('userManagement.deleteUser.deleteTitle')
              : t('userManagement.deleteUser.removeTitle')
          }
          description={
            roleId === -1
              ? t('userManagement.deleteUser.deleteDescription')
              : t('userManagement.deleteUser.removeDescription')
          }
          dataTestId="delete-user-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => setIsDeleteUser(false),
              variant: 'secondary',
              dataTestId: 'delete-user',
            },
            {
              id: '2',
              label:
                roleId === -1
                  ? t('userManagement.deleteUser.deleteConfirm')
                  : t('userManagement.deleteUser.removeConfirm'),
              onClick: ConfirmDelete,
              variant: 'primary',
              dataTestId: 'confirm-delete',
            },
          ]}
        />
      )}
      {showToast && (
        <ArrToast
          id="toast-1"
          title={
            deleteUserError || removeUserError || deleteError
              ? 'Error!'
              : 'Success!'
          }
          message={
            errorMessage ||
            (roleId === -1
              ? deleteUserError?.message || deleteUserData?.message
              : removeUserError?.message || removeUserData?.message)
          }
          type={
            deleteUserError || removeUserError || deleteError
              ? 'error'
              : 'success'
          }
          duration={TOAST_DURATION}
          onClose={handleCloseToast}
          dismissable={true}
          closeIcon={<CloseToast />}
          position="bottom-right"
        />
      )}
      {isEditViewOpen && (
        <EditUser
          userId={selectedUser?.userId ?? 0}
          projectId={selectedUser?.projectID ?? 0}
          entityId={selectedUser?.entityID ?? 0}
          showDrawer={isEditViewOpen}
          handleDrawer={(visible) => setIsEditViewOpen(visible)}
          onUserEdited={refetchUsers}
        />
      )}
    </div>
  );
};

export default ViewUsers;
